import { Injectable } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import ValidationResultModel from '@app/models/validation-result-model';

@Injectable()
export class ValidationService {
  private readonly _componentName = 'ValidationService';

  constructor() {}

  /**
   * Checks if a value is numeric
   * @param value The value to check
   * @returns true if the value is numeric, false otherwise
   */
  private isNumeric(value: any): boolean {
    if (value === null || value === undefined) {
      return false;
    }
    return !isNaN(value) && !isNaN(parseFloat(value));
  }

  validateMonthDigit(text: string) {
    let result = { isValid: true, issues: [] } as ValidationResultModel;

    if (this.isNumeric(text)) {
      let month = text as any as number;

      if (month < 1 || month > 12) {
        let err = 'Month values must be between 1 and 12.';
        console.error(`parseExpiry error: ${err}`);
        result.isValid = false;
        result.issues.push(err);
      }
    } else {
      let err = 'Month values must be numeric.';
      // console.error(`parseExpiry error: ${err}`);

      result.isValid = false;
      result.issues.push(err);
    }

    return result;
  }

  /**
   * Parsed a phone value string and returns string with only digits
   * @param phoneValueString
   */
  parsePhoneNumberString(phoneValueString: string) {
    let cleanedPhoneValueString = phoneValueString.replace(/\D/g, '');
    return cleanedPhoneValueString;
  }

  /**
   * A phone number validator that only looks at the digits
   * in a phone number string
   */
  phoneNumberValidator = (phoneControl: UntypedFormControl) => {
    if (phoneControl) {
      // console.log('begin phoneNumberValidator');
      // 

      const phoneValue = phoneControl.value;
      if (!phoneValue) return null;

      let phoneValueString = phoneValue as string;
      // console.log(`Validating phone value: ${phoneValue}`);

      let cleanedPhoneValueString = this.parsePhoneNumberString(phoneValueString);

      return this.validatePhoneNumberString(cleanedPhoneValueString);
    }

    return null;
  };

  /**
   * Validates a string as a phone number. Returns  ValidationResultModel
   * Empty v parameter does not fail validation.
   */
  public validatePhoneNumber(v: string | any) {
    let result = {
      isValid: false,
      issues: [],
    } as ValidationResultModel;

    // console.log(`${this._componentName}.validatePhoneNumber`);

    const x = (v + '').replace(/\D/g, '');

    if (!x) {
      result.isValid = true;
      return result;
    }

    if (x.length === 10) {
      // pass 10-digit phone numbers
      result.isValid = true;
    } else if (x.length === 11 && x.startsWith('1')) {
      // pass 11-digit phone numbers that start with 1
      result.isValid = true;
    } else {
      const phoneNumberValidationResult = this.validatePhoneNumberString(x);

      if (phoneNumberValidationResult) {
        result.isValid = false;
        result.issues.push(phoneNumberValidationResult.phone);
      } else {
        result.isValid = true;
      }
    }

    return result;
  }

  /**
   * A select dropdown validator that ensures a value is selected
   * first value (0) and no selection are invalid
   */
  selectedItemValidator = (selectControl: UntypedFormControl) => {
    const selectedValue = selectControl.value;

    if (selectedValue === 0 || selectedValue === null) {
      const errorMessage = 'Please make a valid selection';
      console.error(errorMessage);
      return { emailSubject: errorMessage };
    }
    return null;
  };

  /**
   * Validates a string representation of a phone number (US 10 digit phone numbers)
   * Returns an null if valid, error object if it is not
   * @param phoneValueString
   */
  private validatePhoneNumberString(phoneValueString: string) {
    // check for string length
    if (phoneValueString.length > 10) {
      const errorMessage = 'Phone Number should have 10 digits.';
      console.error(errorMessage);
      return { phone: errorMessage };
    }

    if (phoneValueString.length < 10) {
      const errorMessage = 'Phone Number should have 10 digits.';
      console.error(errorMessage);
      return { phone: errorMessage };
    }

    return null;
  }

  /**
   * Middle name validator that supports a "noMiddleName" boolean (checkbox)
   */
  middleNameValidator = (fg: UntypedFormGroup, errorMessage: string) => {
    // console.log('begin MiddleNameValidator');
    const middleName = fg.get('middleName').value;
    const cb = fg.get('noMiddleName').value;

    // console.log('cb',cb);
    // console.log('middleName',middleName);

    if (middleName && !cb) {
      return null;
    }

    if (!middleName && cb) {
      return null;
    }

    if (!errorMessage) errorMessage = 'A middle name must be provided.';

    // console.error(errorMessage);
    return { middleName: errorMessage };
  };

  public dateRangeValidator(startDateTimeFormControlName = 'startDateTime', endDateTimeFormControlName = 'endDateTime'): ValidatorFn {
    return (group: FormGroup): ValidationErrors => {
      // console.log('Begin dateRangeValidator');

      const startDateTimeControl = group.controls[startDateTimeFormControlName];
      const endDateTimeControl = group.controls[endDateTimeFormControlName];

      if (!startDateTimeControl.value) {
        startDateTimeControl.setErrors(null);
      }

      if (!endDateTimeControl.value) {
        endDateTimeControl.setErrors(null);
      }

      if (startDateTimeControl.value && endDateTimeControl.value) {
        if (endDateTimeControl.value < startDateTimeControl.value) {
          endDateTimeControl.setErrors({ endDateTimeFormControlName: 'End value is earlier than Start value.' });
        }

        if (startDateTimeControl.value > endDateTimeControl.value) {
          startDateTimeControl.setErrors({ startDateTimeFormControlName: 'Start value is later than End value.' });
        }

        if (startDateTimeControl.value < endDateTimeControl.value) {
          endDateTimeControl.setErrors(null);
          startDateTimeControl.setErrors(null);
        }
      }

      // if (control1.value !== control2.value) {
      //    control2.setErrors({notEquivalent: true});
      // } else {
      //    control2.setErrors(null);
      // }
      return;
    };
  }

  /**
   * Validates an email address value
   * @param s
   */
  public validateEmailAddress(s: string) {
    let result = {
      isValid: false,
      issues: [],
    } as ValidationResultModel;

    if (!s) {
      result.isValid = false;
      result.issues.push('Value is null/empty.');
    }

    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    if (!re.test(String(s).toLowerCase())) {
      result.issues.push('Invalid Email Address.');
      result.isValid = false;
    } else {
      result.isValid = true;
    }

    return result;
  }
}
